class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.skuCache = new Map();
    this.skuMatch = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus() {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      this.onChange();
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.skuMatch = null;
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  /* --- Artikelnummer/SKU-hantering ---------------------------------- */

  // Känner igen sannolika artikelnummer: en token utan mellanslag,
  // minst 4 tecken, innehåller minst en siffra (t.ex. 91103726, AB-1234).
  looksLikeSku(q) {
    const t = q.trim();
    if (!t || /\s/.test(t)) return false;
    return /^[A-Za-z0-9][A-Za-z0-9\-_.\/]{3,}$/.test(t) && /\d/.test(t);
  }

  // Slår upp exakt SKU-match via den dolda skujson-viewen.
  skuLookup(query) {
    const needle = query.trim().toLowerCase();
    if (this.skuCache.has(needle)) {
      return Promise.resolve(this.skuCache.get(needle));
    }
    const url = new URL(this.getAttribute('action'), window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('view', 'skujson');
    // Ta med slutsålda/coming-soon i artikelnummer-uppslaget (butikens sök gömmer dem annars).
    url.searchParams.set('options[unavailable_products]', 'show');
    return fetch(url.toString())
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => {
        if (!text) return null;
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          return null;
        }
        const products = (data && data.products) || [];
        const exact = products.find((p) =>
          (p.skus || []).some((s) => String(s || '').trim().toLowerCase() === needle)
        );
        const match = exact || null;
        this.skuCache.set(needle, match);
        return match;
      })
      .catch(() => null);
  }

  escapeHtml(str) {
    return String(str == null ? '' : str).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  renderSkuMatch(product) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const target = document.getElementById(id);
    if (!target) return;

    const heading = this.escapeHtml(this.dataset.productsLabel || 'Products');
    const url = this.escapeHtml(product.url || '#');
    const title = this.escapeHtml(product.title || '');
    const price = this.escapeHtml(product.price || '');
    let img = product.image || '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;

    const imgHtml = img
      ? `<a href="${url}" class="media-wrapper blocks-radius-sm"><img src="${this.escapeHtml(
          img
        )}" alt="${title}" loading="lazy" width="100"></a>`
      : '';

    target.innerHTML = `
      <div class="grid gap-6 w-full">
        <div class="grid predictive-search-result search__box-item predictive-search-result--products gap-3">
          <h4 class="predictive-search-result__heading h5">${heading}</h4>
          <ul class="grid gap-4" role="group">
            <li class="predictive-search__list-item" role="option" aria-selected="true">
              <div class="flex items-center gap-3">
                ${imgHtml}
                <div class="grid gap-1">
                  <p class="block m-0">
                    <a href="${url}" class="reversed-link text-pcard-title">${title}</a>
                  </p>
                  <div class="text-pcard-title">${price}</div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>`;

    this.searchContent?.classList.remove('hidden');
    this.removeAttribute('loading');
    this.resetButton.classList.remove('btn--loading');
    this.setAttribute('results', 'true');
  }

  /* ------------------------------------------------------------------ */

  onChange() {
    const query = this.getQuery();
    if (query.length === 0) {
      this.clear();
      return;
    }

    if (this.looksLikeSku(query)) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add('btn--loading');
      this.skuLookup(query).then((match) => {
        // Ignorera om användaren hunnit ändra frågan under tiden
        if (this.getQuery() !== query) return;
        if (match) {
          this.skuMatch = { query, url: match.url };
          this.renderSkuMatch(match);
        } else {
          this.skuMatch = null;
          this.renderSection(this.setupURL().toString());
        }
      });
      return;
    }

    this.skuMatch = null;
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  onSubmit(event) {
    const query = this.getQuery();
    if (!this.looksLikeSku(query)) return; // vanlig fritextsökning – låt formuläret gå som vanligt

    event.preventDefault();

    if (this.skuMatch && this.skuMatch.query === query && this.skuMatch.url) {
      window.location.href = this.skuMatch.url;
      return;
    }

    this.skuLookup(query).then((match) => {
      if (match && match.url) {
        window.location.href = match.url;
      } else {
        // Ingen exakt SKU-träff – gå vidare till vanlig sökresultatsida
        HTMLFormElement.prototype.submit.call(this);
      }
    });
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
